import { Item, ItemActions, ItemHeader, ItemTitle } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { ExternalLinkIcon, XIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AddRulesDialog } from '../AddRulesDialog/AddRulesDialog';
import type { RuleWithPath } from '../AddRulesDialog/types';

/** Строит путь отображения правила: Отчёт → Блок → ... → Правило */
const buildRulePath = (rule: RuleWithPath): string => {
  const parts = [rule.expertReportName];

  if (rule.blockPath && rule.blockPath.length > 0) {
    parts.push(...rule.blockPath);
  } else {
    parts.push(rule.expertReportBlockName);
  }

  parts.push(rule.name);
  return parts.join(' \u2192 ');
};

type RulesSectionProps = {
  rules: RuleWithPath[];
  onRemoveRule: (ruleId: number) => void;
  onAddRules: (rules: RuleWithPath[]) => void;
  onNavigateToReport: (expertReportId: number, blockId: number) => void;
};

export const RulesSection: FC<RulesSectionProps> = ({ rules, onRemoveRule, onAddRules, onNavigateToReport }) => {
  const { t } = useTranslation('coaching');

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">{t('rules')}</div>

      {rules.length > 0 ? (
        <div className="flex flex-col gap-1">
          {rules.map((rule) => (
            <Item key={rule.id} variant="outline" size="sm">
              <ItemHeader>
                <ItemTitle className="text-xs">{buildRulePath(rule)}</ItemTitle>
                <ItemActions className="ml-4 shrink-0">
                  <IconButton
                    variant="ghost"
                    size="icon"
                    onClick={() => onNavigateToReport(rule.expertReportId, rule.expertReportBlockId)}
                    label={t('goToExpertReport')}
                    icon={<ExternalLinkIcon className="size-3.5" />}
                  />
                  <IconButton
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveRule(rule.id)}
                    label={t('removeRule')}
                    icon={<XIcon className="size-3.5" />}
                  />
                </ItemActions>
              </ItemHeader>
            </Item>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">{t('noRules')}</div>
      )}

      <AddRulesDialog linkedRuleIds={rules.map((r) => r.id)} onAddRules={onAddRules} />
    </div>
  );
};
